import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './store-deals.css';
import Pagination from './pagination';
import { useShoppingList } from './shoppinglistcontext';

function StoreDeals() {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToList, shoppingList, removeFromList, increaseQuantity, decreaseQuantity } = useShoppingList();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]);
  const [filteredDeals, setFilteredDeals] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const apiBaseUrl = process.env.REACT_APP_RAILWAY_URL || 'http://localhost:8000';
  const [newStoreSearch, setNewStoreSearch] = useState('');
  const [storeSuggestions, setStoreSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [showShoppingSidebar, setShowShoppingSidebar] = useState(false);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [storeSearchResults, setStoreSearchResults] = useState([]);
  const [storeSearchLoading, setStoreSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    // Get data from navigation state
    if (location.state) {
      const { stores } = location.state;
      setSelectedStores(stores || []);
      if (stores && stores.length > 0) {
        fetchDealsForStores(stores);
      }
    } else {
      // If no state, redirect back to deals page
      navigate('/deals');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, navigate]);

  // Fetch deals from database for selected stores
  const fetchDealsForStores = async (storeList) => {
    if (storeList.length === 0) {
      setDeals([]);
      setFilteredDeals([]);
      return;
    }

    setLoading(true);
    try {
      const storeNames = storeList.map(store => store.name);
      console.log('Fetching deals for stores:', storeNames); // Debug log
      
      const response = await fetch(`${apiBaseUrl}/api/search-deals/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stores: storeNames })
      });

      const data = await response.json();
      console.log('Deals API response:', data); // Debug log
      
      if (data.success) {
        const dealsData = data.deals || [];
        console.log('Setting deals:', dealsData); // Debug log
        setDeals(dealsData);
        setFilteredDeals(dealsData);
      } else {
        console.error('Error fetching deals:', data.error);
        setDeals([]);
        setFilteredDeals([]);
      }
    } catch (err) {
      console.error('Error fetching deals:', err);
      setDeals([]);
      setFilteredDeals([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = deals;
    
    // Apply category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(deal => 
        deal.category && deal.category === filterCategory
      );
    }
    
    // Apply brand filter
    if (filterBrand !== 'all') {
      filtered = filtered.filter(deal => 
        deal.brand && deal.brand.toLowerCase().includes(filterBrand.toLowerCase())
      );
    }
    
    setFilteredDeals(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleCategoryFilter = (category) => {
    setFilterCategory(category);
  };

  const handleBrandFilter = (brand) => {
    setFilterBrand(brand);
  };

  // Apply filters whenever filter values change
  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory, filterBrand, deals]);

  const handleSort = (sortType) => {
    setSortBy(sortType);
    const sorted = [...filteredDeals].sort((a, b) => {
      switch (sortType) {
        case 'name':
          return (a.product_name || '').localeCompare(b.product_name || '');
        case 'price':
          // Always use the price column value for sorting
          const priceA = parseFloat(a.price?.replace(/[^\d.]/g, '') || 0);
          const priceB = parseFloat(b.price?.replace(/[^\d.]/g, '') || 0);
          return priceA - priceB;
        case 'store':
          return (a.store || '').localeCompare(b.store || '');
        case 'discount':
          const discountA = parseFloat(a.savings?.replace(/[^\d.]/g, '') || 0);
          const discountB = parseFloat(b.savings?.replace(/[^\d.]/g, '') || 0);
          return discountB - discountA;
        default:
          return 0;
      }
    });
    setFilteredDeals(sorted);
  };

  const getUniqueCategories = () => {
    const categories = deals.map(deal => deal.category).filter(category => category && category.trim() !== '');
    return [...new Set(categories)];
  };

  const getUniqueBrands = () => {
    const brands = deals.map(deal => deal.brand).filter(brand => brand && brand.trim() !== '');
    return [...new Set(brands)];
  };

  const getCategoryDisplayName = (category) => {
    const categoryNames = {
      'dairy': 'Dairy',
      'fish': 'Fish',
      'meat_bird': 'Meat and Bird',
      'vegetarian': 'Vegetarian',
      'vegan': 'Vegan',
      'fruits': 'Fruits',
      'vegetables': 'Vegetables',
      'dry_goods': 'Dry Goods'
    };
    return categoryNames[category] || category;
  };

  const addStore = (storeName) => {
    if (storeName && !selectedStores.find(s => s.name.toLowerCase() === storeName.toLowerCase())) {
      const newStore = {
        id: `manual-${Date.now()}`,
        name: storeName,
        distance: '0.0'
      };
      const updatedStores = [...selectedStores, newStore];
      setSelectedStores(updatedStores);
      fetchDealsForStores(updatedStores);
      setNewStoreSearch('');
    }
  };

  const removeStore = (storeId) => {
    const updatedStores = selectedStores.filter(store => store.id !== storeId);
    setSelectedStores(updatedStores);
    if (updatedStores.length > 0) {
      fetchDealsForStores(updatedStores);
    } else {
      setDeals([]);
      setFilteredDeals([]);
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDeals = filteredDeals.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Fetch store suggestions
  const fetchStoreSuggestions = async (query) => {
    if (!query.trim()) {
      setStoreSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/api/search-store-suggestions/?query=${encodeURIComponent(query)}&limit=10`);
      const data = await response.json();
      
      if (data.suggestions) {
        setStoreSuggestions(data.suggestions);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Error fetching store suggestions:', error);
      setStoreSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle store search input change
  const handleStoreSearchChange = (e) => {
    const value = e.target.value;
    setNewStoreSearch(value);
    setSelectedSuggestionIndex(-1);
    
    // Debounce the API call
    if (value.length >= 2) {
      setTimeout(() => {
        fetchStoreSuggestions(value);
      }, 300);
    } else {
      setStoreSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection
  const selectSuggestion = (suggestion) => {
    setNewStoreSearch(suggestion.name);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    addStore(suggestion.name);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < storeSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          selectSuggestion(storeSuggestions[selectedSuggestionIndex]);
        } else {
          addStore(newStoreSearch);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
      default:
        break;
    }
  };

  // Close suggestions when clicking outside
  const handleInputBlur = () => {
    // Small delay to allow click events on suggestions
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }, 200);
  };

  // Handle adding items to shopping list
  const handleAddToShoppingList = (deal) => {
    // Convert deal format to match shopping list expectations
    // Always use the price column value
    const displayPrice = deal.price ? `${deal.price} kr` : '0 kr';
    
    const shoppingListItem = {
      id: deal.id,
      store: deal.store,
      product: deal.product_name,
      price: displayPrice,
      originalPrice: deal.jfr_pris && deal.jfr_pris.trim() !== '' ? deal.jfr_pris : null,
      brand: deal.brand && deal.brand.trim() !== '' ? deal.brand : '',
      size: deal.amount && deal.amount.trim() !== '' ? deal.amount : '',
      image: '/images/placeholder-product.png', // Using placeholder since we don't have product images
      category: deal.category || '',
      description: deal.description || '',
      campaignEnd: deal.campaign_end
    };
    
    addToList(shoppingListItem);
  };

  // Check if item is already in shopping list
  const isInShoppingList = (dealId) => {
    return shoppingList.some(item => item.id === dealId);
  };

  // Shopping list helper functions
  const calculateTotalCost = () => {
    const total = shoppingList.reduce((total, item) => {
      // Extract numeric value from price, handle comma as decimal separator
      let priceStr = item.price?.replace(/\s*kr\s*/g, '') || '0'; // Remove 'kr' and spaces
      priceStr = priceStr.replace(',', '.'); // Replace comma with dot for decimal
      const numericPrice = parseFloat(priceStr || 0);
      const quantity = item.quantity || 1;
      return total + (numericPrice * quantity);
    }, 0);
    return total;
  };

  const getUniqueStores = () => {
    const stores = shoppingList.map(item => item.store).filter(store => store);
    return [...new Set(stores)];
  };

  const handleAddToNotes = () => {
    // TODO: Implement notes functionality
    alert('Add to notes functionality will be implemented soon!');
  };

  const handleViewStore = async (deal) => {
    setSelectedProduct(deal);
    setStoreSearchLoading(true);
    setShowStoreModal(true);
    
    try {
      const response = await fetch(`${apiBaseUrl}/api/search-product-stores/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_name: deal.product_name })
      });

      const data = await response.json();
      
      if (data.success) {
        setStoreSearchResults(data.stores || []);
      } else {
        console.error('Error searching product stores:', data.error);
        setStoreSearchResults([]);
      }
    } catch (err) {
      console.error('Error searching product stores:', err);
      setStoreSearchResults([]);
    } finally {
      setStoreSearchLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="store-deals-page">
        <div className="store-loading-container">
          <div className="store-loading-spinner"></div>
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="store-deals-page">
      {/* Left Sidebar */}
      <div className="sidebar">
        {/* Back Button */}
        <button className="back-button" onClick={() => navigate('/deals')}>
          ← Back to Search
        </button>
        
        {/* Selected Stores Section */}
        <div className="sidebar-section">
          <h3>Selected Stores ({selectedStores.length})</h3>
          <div className="selected-stores-list">
            {selectedStores.map(store => (
              <div key={store.id} className="store-item">
                <span className="store-name">{store.name}</span>
                {/* <span className="store-distance">({store.distance}km)</span> */}
                <button 
                  className="remove-store-btn"
                  onClick={() => removeStore(store.id)}
                  title="Remove store"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          
          {/* Add Store */}
          <div className="add-store-section">
            <div className="store-search-container">
              <input
                type="text"
                placeholder="Add store by name..."
                value={newStoreSearch}
                onChange={handleStoreSearchChange}
                onKeyDown={handleKeyDown}
                onBlur={handleInputBlur}
                className="add-store-input"
              />
              {showSuggestions && storeSuggestions.length > 0 && (
                <div className="suggestions-dropdown">
                  {storeSuggestions.map((suggestion, index) => (
                    <div
                      key={suggestion.id}
                      className={`suggestion-item ${index === selectedSuggestionIndex ? 'selected' : ''}`}
                      onClick={() => selectSuggestion(suggestion)}
                    >
                      <div className="suggestion-name">{suggestion.name}</div>
                      {suggestion.city && (
                        <div className="suggestion-location">{suggestion.city}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button 
              className="add-store-btn"
              onClick={() => addStore(newStoreSearch)}
              disabled={!newStoreSearch.trim()}
            >
              Add
            </button>
          </div>
        </div>

        {/* Filters Section */}
        <div className="sidebar-section">
          <h3>Filters</h3>
          
          <div className="filter-group">
            <label>Filter by Category:</label>
            <select 
              value={filterCategory} 
              onChange={(e) => handleCategoryFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Categories</option>
              {getUniqueCategories().map(category => (
                <option key={category} value={category}>
                  {getCategoryDisplayName(category)}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Filter by Brand:</label>
            <select 
              value={filterBrand} 
              onChange={(e) => handleBrandFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Brands</option>
              {getUniqueBrands().map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Sort by:</label>
            <select 
              value={sortBy} 
              onChange={(e) => handleSort(e.target.value)}
              className="filter-select"
            >
              <option value="name">Name</option>
              <option value="price">Price</option>
              <option value="store">Store</option>
              <option value="discount">Discount</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <div className="content-header">
          <div className="header-left">
            <h1>Store Deals</h1>
            <p>Showing {currentDeals.length} of {filteredDeals.length} deals</p>
          </div>
          <div className="header-right">
            <button 
              className="shopping-list-button"
              onClick={() => setShowShoppingSidebar(!showShoppingSidebar)}
            >
              🛒 Shopping List ({shoppingList.length})
            </button>
          </div>
        </div>

        {loading ? (
          <div className="store-loading-container">
            <div className="store-loading-spinner"></div>
            <p>Loading deals...</p>
          </div>
        ) : currentDeals.length > 0 ? (
          <>
            <div className="store-deals-grid">
              {currentDeals.map(deal => (
                <div key={deal.id} className="store-deal-card">
                  <div className="store-deal-image">
                    <img 
                      src="/images/placeholder-product.png"
                      alt={deal.product_name}
                      onError={(e) => {
                        e.target.src = '/images/placeholder-product.png';
                      }}
                    />
                    <div className="store-badge">{deal.store}</div>
                  </div>
                  
                  <div className="store-deal-info">
                    <div className="store-deal-content">
                      <h3 className="store-deal-name">{deal.product_name}</h3>
                      <p className="store-deal-brand">{deal.brand || '\u00A0'}</p>
                      <p className="store-deal-amount">{deal.amount || '\u00A0'}</p>
                      <div className="store-category-container">
                        {deal.category && (
                          <span 
                            className="store-category-badge" 
                            data-category={deal.category}
                          >
                            {getCategoryDisplayName(deal.category)}
                          </span>
                        )}
                      </div>
                      
                      <div className="store-deal-pricing">
                        <div className="store-price-row">
                          {/* Check if kampanjpris has value to show prefix, but always display price column */}
                          {deal.price && deal.price.trim() !== '' && (
                            <>
                              {deal.kampanjpris && deal.kampanjpris.trim() !== '' ? (
                                <>
                                  <span className="store-price-prefix">Kampanjpris: </span>
                                  <span className="store-price-value">{deal.price} kr</span>
                                </>
                              ) : deal.klubbpris && deal.klubbpris.trim() !== '' ? (
                                <>
                                  <span className="store-price-prefix">Klubbpris: </span>
                                  <span className="store-price-value">{deal.price} kr</span>
                                </>
                              ) : (
                                <span className="store-price-value">{deal.price} kr</span>
                              )}
                            </>
                          )}
                          
                          {/* Show jfr_pris next to the price */}
                          {deal.jfr_pris && (
                            <span className="store-jfr-price">{deal.jfr_pris}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="store-deal-dates">
                        <small>
                          Valid: {new Date(deal.campaign_start).toLocaleDateString()} - {new Date(deal.campaign_end).toLocaleDateString()}
                        </small>
                      </div>
                      
                      {deal.description && (
                        <p className="store-deal-description">{deal.description}</p>
                      )}
                    </div>
                    
                    <div className="store-deal-actions">
                      <button 
                        className={`store-add-to-list-btn ${isInShoppingList(deal.id) ? 'added' : ''}`}
                        onClick={() => handleAddToShoppingList(deal)}
                        disabled={isInShoppingList(deal.id)}
                      >
                        {isInShoppingList(deal.id) ? 'Added to List' : 'Add to List'}
                      </button>
                      <button 
                        className="store-view-store-btn"
                        onClick={() => handleViewStore(deal)}
                      >
                        Available at
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {filteredDeals.length > itemsPerPage && (
              <Pagination 
                itemsPerPage={itemsPerPage}
                totalItems={filteredDeals.length}
                currentPage={currentPage}
                paginate={paginate}
              />
            )}
          </>
        ) : (
          <div className="no-deals">
            <h3>No deals found</h3>
            <p>
              {selectedStores.length === 0 
                ? 'Please add some stores to see deals.'
                : 'No deals available for the selected stores. Try adding different stores.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Shopping List Sidebar */}
      {showShoppingSidebar && (
        <div className="shopping-sidebar-overlay" onClick={() => setShowShoppingSidebar(false)}>
          <div className="shopping-sidebar" onClick={(e) => e.stopPropagation()}>
            <div className="shopping-sidebar-header">
              <h3>🛒 Shopping List</h3>
              <button 
                className="close-sidebar-btn"
                onClick={() => setShowShoppingSidebar(false)}
              >
                ×
              </button>
            </div>
            
            <div className="shopping-sidebar-content">
              {shoppingList.length > 0 ? (
                <>
                  <div className="shopping-items">
                    {shoppingList.map(item => (
                      <div key={item.id} className="shopping-item">
                        <div className="item-controls">
                          <button
                            className="remove-item-btn"
                            onClick={() => removeFromList(item.id)}
                            title="Remove entirely from shopping list"
                          >
                            ×
                          </button>
                          <div className="quantity-line">
                            <button
                              className="quantity-btn"
                              onClick={() => decreaseQuantity(item.id)}
                              title="Decrease quantity"
                            >
                              −
                            </button>
                            <span className="quantity-display">{item.quantity || 1}</span>
                            <button
                              className="quantity-btn"
                              onClick={() => increaseQuantity(item.id)}
                              title="Increase quantity"
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <div className="item-info">
                          <h4 className="item-product-name">{item.product}</h4>
                          <div className="item-details">
                            <span className="item-brand-size">
                              {item.brand && item.brand}
                              {item.brand && item.size && ' '}
                              {item.size && item.size}
                            </span>
                          </div>
                          <p className="item-store">from {item.store}</p>
                        </div>
                        <div className="item-pricing">
                          <span className="item-price">{item.price}</span>
                          {item.originalPrice && (
                            <span className="item-jfr-price">{item.originalPrice}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="shopping-summary">
                    <div className="summary-row">
                      <span>Total Cost:</span>
                      <span className="summary-value">
                        {calculateTotalCost().toFixed(2).replace('.', ',')} kr
                      </span>
                    </div>
                    <div className="summary-row">
                      <span>Total Items:</span>
                      <span className="summary-value">{shoppingList.length}</span>
                    </div>
                    <div className="summary-row">
                      <span>Stores:</span>
                      <span className="summary-value">{getUniqueStores().length}</span>
                    </div>
                    <div className="summary-stores">
                      {getUniqueStores().map(store => (
                        <span key={store} className="store-tag">{store}</span>
                      ))}
                    </div>
                    
                    <button 
                      className="add-to-notes-btn"
                      onClick={handleAddToNotes}
                    >
                      Add to Notes
                    </button>
                  </div>
                </>
              ) : (
                <div className="empty-shopping-list">
                  <p>Your shopping list is empty</p>
                  <p>Add items by clicking "Add to List" on any deal</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Store Search Modal */}
      {showStoreModal && (
        <div className="store-modal-overlay" onClick={() => setShowStoreModal(false)}>
          <div className="store-modal" onClick={(e) => e.stopPropagation()}>
            <div className="store-modal-header">
              <h3>Stores with "{selectedProduct?.product_name}"</h3>
              <button 
                className="close-modal-btn"
                onClick={() => setShowStoreModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="store-modal-content">
              {storeSearchLoading ? (
                <div className="store-modal-loading">
                  <div className="store-loading-spinner"></div>
                  <p>Searching stores...</p>
                </div>
              ) : storeSearchResults.length > 0 ? (
                <div className="stores-list">
                  {storeSearchResults.map((storeData, index) => (
                    <div key={index} className="store-result">
                      <div className="store-result-header">
                        <h4>{storeData.store_name}</h4>
                        <span className="product-count">{storeData.campaign_count} product(s)</span>
                      </div>
                      
                      <div className="store-products">
                        {storeData.products.map((product, prodIndex) => (
                          <div key={prodIndex} className="product-result">
                            <div className="product-info">
                              <p className="product-name">{product.product_name}</p>
                              {product.brand && <p className="product-brand">{product.brand}</p>}
                              {product.amount && <p className="product-amount">{product.amount}</p>}
                              
                              <div className="product-pricing">
                                {product.price && (
                                  <>
                                    {product.kampanjpris && product.kampanjpris.trim() !== '' ? (
                                      <>
                                        <span className="price-prefix">Kampanjpris: </span>
                                        <span className="price-value">{product.price} kr</span>
                                      </>
                                    ) : product.klubbpris && product.klubbpris.trim() !== '' ? (
                                      <>
                                        <span className="price-prefix">Klubbpris: </span>
                                        <span className="price-value">{product.price} kr</span>
                                      </>
                                    ) : (
                                      <span className="price-value">{product.price} kr</span>
                                    )}
                                    
                                    {product.jfr_pris && (
                                      <span className="jfr-price">{product.jfr_pris}</span>
                                    )}
                                  </>
                                )}
                              </div>
                              
                              <div className="product-dates">
                                <small>
                                  Valid: {new Date(product.campaign_start).toLocaleDateString()} - {new Date(product.campaign_end).toLocaleDateString()}
                                </small>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-stores-found">
                  <p>No other stores found with this product</p>
                  <p>This might be the only store currently offering "{selectedProduct?.product_name}"</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StoreDeals; 